import type { ShopDetailsPageViewModel } from '@lib/page-data/shopDetails.map';
import type { Locale } from '@lib/locale';
import styled from 'styled-components';
import type { PageInjectedProps } from '../Pages/Page';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import { BannerBackgroundImg } from '../shared/BannerBackgroundImgDiv';
import { makeExternalUrl } from '../../../../lib/utils';
import ShopInfoCard from './ShopInfoCard';
import ShopDetails from './ShopDetails';
import ShopDescriptions from './ShopDescriptions';
import RenderLayoutViews from '../shared/RenderLayoutViews';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';

type Shop = Exclude<ShopDetailsPageViewModel['shopData'], Record<string, never>>;
type Props = Partial<Shop>
  & Pick<ShopDetailsPageViewModel, 'parentCategories' | 'subCategories' | 'shopConfig'>
  & { locale: Locale }
  & Partial<Pick<PageInjectedProps, 'userData' | 'pageStateModifiers'>>;

const PAGE_CONTEXT = 'shop-page';

const ShopBannerBackgroundImg = styled(BannerBackgroundImg)`
  width: 100%;
  min-width: 100%;
  object-fit: cover;
  min-height: ${(p) => p.theme.unit(45)};
  display: block;
`;

const ShopMobileBannerBackgroundImg = styled(BannerBackgroundImg)`
  width: 100%;
  min-width: 100%;
  object-fit: cover;
  min-height: ${(p) => p.theme.unit(15)};
  height: ${(p) => p.theme.unit(15)};
  display: block;
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: ${(p) => p.theme.unit(6)};
  margin-bottom: ${(p) => p.theme.unit(5)};
`;

const ColDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const LayoutViewsContainerDiv = styled.div`
  margin-bottom: ${(p) => p.theme.unit(6)};
`;

const DividerDiv = styled.div<{ showBorder: boolean }>`
  border-bottom: ${(p) => (p.showBorder ? `1px solid ${p.theme.c.border.primary}` : 'none')};
`;

const showBottomBorder = (layouts: NonNullable<Props['layout']>) => layouts.some((layoutView) => {
  if (!layoutView) return false;
  const contentType = 'contentType' in layoutView ? layoutView.contentType ?? '' : '';
  const offers = 'offers' in layoutView && Array.isArray(layoutView.offers) ? layoutView.offers : [];
  const shops = 'shops' in layoutView && Array.isArray(layoutView.shops) ? layoutView.shops : [];
  const contentTypes = ['adContainer', 'layoutLifestyleBanner', 'layoutSmallPromoBanner'];
  return contentTypes.includes(contentType) || offers.length > 0 || shops.length > 0;
});

const ShopPage = (props: Props) => {
  const {
    heroBannerImageUrl,
    currency,
    deliveryInformation,
    categories,
    config,
    locale,
    name = '',
    largeDescription = '',
    importantInformation = '',
    layout = [],
    userData = {},
    pageStateModifiers = {},
    shopConfig = [],
    parentCategories = [],
    subCategories = [],
  } = props;

  const sectionTitleForOffers = shopConfig?.[0]?.sectionTitleForOffers;

  const externalUrl: unknown = makeExternalUrl(heroBannerImageUrl);
  const resolvedHeroImageUrl = typeof externalUrl === 'string' ? externalUrl : '';
  const showBorder = showBottomBorder(layout ?? []);

  const desktopShopPage = (
    <div>
      <ShopBannerBackgroundImg src={resolvedHeroImageUrl} alt="" />
      <MaxWidthLayout>
        <RowDiv>
          <ColDiv>
            <ShopInfoCard {...props} pageContext={`${PAGE_CONTEXT}-store`} />
            <ShopDetails
              {...config}
              currency={currency}
              deliveryInformation={deliveryInformation}
              categories={categories}
              parentCategories={parentCategories}
              subCategories={subCategories}
              locale={locale}
            />
          </ColDiv>
          <ColDiv>
            <ShopDescriptions
              {...config}
              name={name}
              largeDescription={largeDescription}
              importantInformation={importantInformation}
            />
          </ColDiv>
        </RowDiv>
        <DividerDiv showBorder={showBorder} />
      </MaxWidthLayout>
      <section id="store-info-bottom-section">
        {showBorder && (
        <LayoutViewsContainerDiv>
          <RenderLayoutViews
            views={layout}
            locale={locale}
            userData={userData}
            sectionTitle={sectionTitleForOffers}
            pageStateModifiers={pageStateModifiers}
            pageContext={`${PAGE_CONTEXT}-offers`}
          />
        </LayoutViewsContainerDiv>
        )}
      </section>
    </div>
  );

  const mobileShopPage = (
    <div>
      <ShopMobileBannerBackgroundImg src={resolvedHeroImageUrl} alt="" />
      <MaxWidthLayout>
        <ShopInfoCard isMobile {...props} pageContext={`${PAGE_CONTEXT}-store`} />
        <ShopDescriptions
          isMobile
          {...config}
          name={name}
          largeDescription={largeDescription}
          importantInformation={importantInformation}
        />
        <ShopDetails
          isMobile
          {...config}
          currency={currency}
          deliveryInformation={deliveryInformation}
          categories={categories}
          parentCategories={parentCategories}
          subCategories={subCategories}
          locale={locale}
        />
        <DividerDiv showBorder={showBorder} />
      </MaxWidthLayout>
      <section id="store-info-bottom-section">
        {showBorder && (
          <RenderLayoutViews
            views={layout}
            locale={locale}
            userData={userData}
            sectionTitle={sectionTitleForOffers}
            pageStateModifiers={pageStateModifiers}
            pageContext={`${PAGE_CONTEXT}-offers`}
          />
        )}
      </section>
    </div>
  );

  const renderComp = (
    <MediaQueryHelper
      desktopChildren={desktopShopPage}
      mobileChildren={mobileShopPage}
    />
  );
  return renderComp;
};

export default ShopPage;
